import { type ReactElement, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import { useMediaQuery, useTheme } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"
import _, { truncate } from "lodash"
import moment from "moment"

import { KonanCopyToClipboard } from "../../../../components/utility/KonanCopyToClipboard"
import type { PredictionReview } from "../../../../types/generated/api/PredictionReview"
import { DecisionsContext } from "../../context/Context"
import type { MetaDataProps } from "../../interfaces"
import { ReviewActions } from "../Elements/ReviewActions"

import styles from "./MetaData.module.scss"

type PendingSelection = "first" | "last" | null

/**
 * Metadata panel with pagination-aware navigation between predictions.
 */
export function MetaData(props: Readonly<MetaDataProps>): ReactElement {
  // props
  const { selectedUuid } = props

  // hooks
  const { t } = useTranslation("decisions", { keyPrefix: "metaData" })
  const { data, selectedRow, setSelectedRow, pagination, setPagination, isFetching, isLoading } =
    useContext(DecisionsContext)
  const theme = useTheme()
  const showNavLabels = useMediaQuery(theme.breakpoints.up("lg"))

  // constants
  const keyLabels: Record<string, string> = {
    uuid: t("decisionId"),
    created_at: t("requestTime"),
    review: t("reviewed"),
  }

  // State Hooks
  const [pendingSelection, setPendingSelection] = useState<PendingSelection>(null)
  const [pendingPage, setPendingPage] = useState<number | null>(null)

  // useRef
  const prevFirstUuidRef = useRef<string | null>(null)
  const prevLengthRef = useRef<number>(0)

  // UseMemo
  const predictions = useMemo(() => data?.results?.outputs ?? [], [data?.results?.outputs])
  const currentIndex = predictions.findIndex((p: { uuid: string }) => p.uuid === selectedUuid)
  const currentPrediction = selectedRow

  // helper functions
  const goPrev = (): void => {
    if (currentIndex > 0) {
      setSelectedRow(predictions[currentIndex - 1] ?? null)
      return
    }

    if (pagination && setPagination && pagination.pageIndex > 0) {
      prevFirstUuidRef.current = predictions[0]?.uuid ?? null
      prevLengthRef.current = predictions.length

      const targetPage = pagination.pageIndex - 1
      setPendingSelection("last")
      setPendingPage(targetPage)
      setPagination({ ...pagination, pageIndex: targetPage })
    }
  }

  const goNext = (): void => {
    if (currentIndex < predictions.length - 1) {
      setSelectedRow(predictions[currentIndex + 1] ?? null)
      return
    }

    if (pagination && setPagination && data?.next) {
      prevFirstUuidRef.current = predictions[0]?.uuid ?? null
      prevLengthRef.current = predictions.length

      const targetPage = (pagination.pageIndex ?? 0) + 1
      setPendingSelection("first")
      setPendingPage(targetPage)
      setPagination({ ...pagination, pageIndex: targetPage })
    }
  }

  const renderFieldContent = (key: string, value: string | PredictionReview | null): React.ReactNode => {
    if (key === "created_at") {
      return (
        <Typography variant="p" variantColor={2}>
          {moment(value as string)
            .utc()
            .format("MMM DD YYYY, H:mm:ss")}
        </Typography>
      )
    } else if (key === "uuid") {
      return (
        <Typography variant="p" noWrap variantColor={2}>
          <KonanCopyToClipboard text={String(value)} />
          {truncate(String(value), { length: 20 })}
        </Typography>
      )
    } else if (key === "review") {
      if (selectedRow) {
        return <ReviewActions original={selectedRow} />
      } else {
        return null
      }
    }
    return null
  }

  const renderMetadataCards = (): React.ReactNode[] => {
    if (!currentPrediction) return []

    const fields: Record<string, string | PredictionReview | null> = {
      uuid: currentPrediction.uuid ?? null,
      created_at: currentPrediction.created_at ?? null,
      review: currentPrediction.review ?? null,
    }

    return Object.entries(fields).map(([key, value]) => {
      const label = keyLabels[key] ?? key
      const content = renderFieldContent(key, value)

      return (
        <div className={styles.card} key={key}>
          <Typography variant="label-regular" className={styles.cardLabel}>
            {label}
          </Typography>
          {content}
        </div>
      )
    })
  }

  // Update selectedRow when data changes
  useEffect(() => {
    if (!data?.results?.outputs) return

    const updated = data.results.outputs.find((p: { uuid: string }) => p.uuid === selectedUuid)
    if (updated) {
      setSelectedRow(updated)
    }
  }, [data, selectedUuid, setSelectedRow])

  // Handle pagination navigation and selection of first/last items
  useEffect(() => {
    if (!pendingSelection || pendingPage === null) return
    if (!pagination) return

    if (pagination.pageIndex !== pendingPage) return

    const firstUuid = predictions[0]?.uuid ?? null
    const isDifferent = predictions.length !== prevLengthRef.current || firstUuid !== prevFirstUuidRef.current

    if (!isDifferent) {
      return
    }

    if (predictions.length === 0) {
      setPendingSelection(null)
      setPendingPage(null)
      return
    }

    if (pendingSelection === "first") {
      setSelectedRow(predictions[0] ?? null)
    } else if (pendingSelection === "last") {
      setSelectedRow(predictions[predictions.length - 1] ?? null)
    }

    setPendingSelection(null)
    setPendingPage(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSelection, pendingPage, pagination?.pageIndex, predictions, setSelectedRow])

  // early returns
  if (!currentPrediction) {
    return (
      <div className={styles.metadataRoot}>
        <Typography variant="p">{t("noPredictionFound", { uuid: selectedUuid })}</Typography>
      </div>
    )
  }

  // render logic constants
  const canPrev = !(currentIndex <= 0 && (!pagination || pagination.pageIndex === 0)) && !isLoading && !isFetching
  const canNext = !(currentIndex >= predictions.length - 1 && !data?.next) && !isLoading && !isFetching

  return (
    <div className={styles.metadataRoot}>
      <div className={styles.metadataWrapper}>
        <Button variant="primary-inverse" className={styles.navButton} onClick={goPrev} disabled={!canPrev}>
          <ChevronLeftIcon className={styles.icon} fontSize="small" />
          {showNavLabels && (
            <Typography variant="span-bold" className={styles.navLabel}>
              {t("priorRequest")}
            </Typography>
          )}
        </Button>
        <div className={styles.scrollArea}>
          <div className={styles.metadata}>{renderMetadataCards()}</div>
        </div>

        <Button variant="primary-inverse" className={styles.navButton} onClick={goNext} disabled={!canNext}>
          {showNavLabels && (
            <Typography variant="span-bold" className={styles.navLabel}>
              {t("nextRequest")}
            </Typography>
          )}
          <KeyboardArrowRightIcon className={styles.icon} fontSize="small" />
        </Button>
      </div>
    </div>
  )
}
